import React from 'react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const ProductShowcase = () => {
  const { ref, animationClass } = useScrollAnimation('fade-in-up', 0.1);

  return (
    <section ref={ref} className={`bg-[#FFF5F2] py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 overflow-hidden relative ${animationClass}`}>
      <div className="w-full max-w-[2400px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900 mb-3 sm:mb-4">
            What <span className="bg-[#E85D44] text-[#FFD93D] px-4 sm:px-6 py-1 sm:py-2 inline-block">Makes it</span> Good?
          </h2>
        </div>

        {/* Product Showcase with Floating Features */}
        <div className="relative w-full mx-auto min-h-[500px] sm:min-h-[700px] md:min-h-[900px] lg:min-h-[1000px] flex items-center justify-center">
          {/* Center Product Image - Smaller and responsive */}
          <div className="relative z-20 w-full max-w-[180px] sm:max-w-[220px] md:max-w-[280px] lg:max-w-[350px] xl:max-w-[400px]">
            <img
              src="/images/product-showcase-lamp.png"
              alt="3D Night Lamp Showcase"
              className="w-full h-auto object-contain drop-shadow-2xl"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>

          {/* PRIMARY Feature Callouts - Main Features (White with Black Border) - Inner Circle */}
          {/* Arranged in 8 positions around the lamp with proper spacing */}

          {/* Top Right - 1:30 o'clock */}
          <div
            className="absolute top-20 sm:top-24 md:top-28 lg:top-32 right-8 sm:right-12 md:right-20 lg:right-28 xl:right-36 z-[35] animate-float"
            style={{ animationDelay: '0s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                2 AA Batteries<br />Included
              </p>
            </div>
          </div>

          {/* Right - 2:30 o'clock (moved up) */}
          <div
            className="absolute top-[38%] sm:top-[40%] right-12 sm:right-16 md:right-24 lg:right-32 xl:right-40 z-[34] animate-float"
            style={{ animationDelay: '1s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                100% Plastic (PLA)
              </p>
            </div>
          </div>

          {/* Bottom Right - 4:30 o'clock */}
          <div
            className="absolute bottom-20 sm:bottom-24 md:bottom-28 lg:bottom-32 right-8 sm:right-12 md:right-20 lg:right-28 xl:right-36 z-[33] animate-float"
            style={{ animationDelay: '1.5s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                Comes with<br />LED Lights
              </p>
            </div>
          </div>

          {/* Bottom - 6 o'clock (moved up and centered) */}
          <div
            className="absolute bottom-16 sm:bottom-20 md:bottom-24 lg:bottom-28 left-1/2 -translate-x-1/2 z-[32] animate-float"
            style={{ animationDelay: '2s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                3D Printing<br />Technology
              </p>
            </div>
          </div>

          {/* Bottom Left - 7:30 o'clock */}
          <div
            className="absolute bottom-20 sm:bottom-24 md:bottom-28 lg:bottom-32 left-8 sm:left-12 md:left-20 lg:left-28 xl:left-36 z-[36] animate-float"
            style={{ animationDelay: '2.5s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                Design Your Own
              </p>
            </div>
          </div>

          {/* Left - 8 o'clock (moved up and closer to center) */}
          <div
            className="absolute top-[38%] sm:top-[40%] left-16 sm:left-20 md:left-28 lg:left-36 xl:left-44 z-[37] animate-float"
            style={{ animationDelay: '3s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                Add up to 4<br />Images
              </p>
            </div>
          </div>

          {/* Top Left - 10:30 o'clock */}
          <div
            className="absolute top-20 sm:top-24 md:top-28 lg:top-32 left-8 sm:left-12 md:left-20 lg:left-28 xl:left-36 z-[31] animate-float"
            style={{ animationDelay: '3.5s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                Custom Text
              </p>
            </div>
          </div>

          {/* Top - 11:30 o'clock (slightly left of center) */}
          <div
            className="absolute top-8 sm:top-12 md:top-16 left-[38%] sm:left-[40%] z-[30] animate-float"
            style={{ animationDelay: '4s' }}
          >
            <div className="bg-white border-3 border-gray-900 rounded-full px-3 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-2 sm:py-3 md:py-4 lg:py-5 shadow-xl hover:shadow-2xl transition-all duration-300">
              <p className="text-xs sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 text-center whitespace-nowrap leading-tight">
                Environmental<br />Friendly
              </p>
            </div>
          </div>

          {/* SECONDARY Callouts - Marketing Messages (Lighter colors) - Outer Circle */}
          {/* Positioned between the primary callouts for better visibility */}
          {/* Hidden on small screens, shown on medium and up */}

          {/* Between 12 and 1:30 - Top area */}
          <div
            className="hidden md:block absolute top-4 sm:top-6 md:top-8 right-[30%] z-[8] animate-float"
            style={{ animationDelay: '3.5s' }}
          >
            <div className="bg-[#FFF9E6] border-2 border-[#FFD93D] rounded-full px-4 md:px-6 lg:px-7 py-1.5 md:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs md:text-sm lg:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Transform memories<br />into glowing art
              </p>
            </div>
          </div>

          {/* Between 1:30 and 3 - Upper Right */}
          <div
            className="hidden lg:block absolute top-[30%] right-2 sm:right-4 md:right-6 lg:right-8 xl:right-12 z-[27] animate-float"
            style={{ animationDelay: '4.5s' }}
          >
            <div className="bg-[#FFF9E6] border-2 border-[#FFD93D] rounded-full px-4 lg:px-5 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs lg:text-sm xl:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Handcrafted<br />with care
              </p>
            </div>
          </div>

          {/* Between 3 and 4:30 - Lower Right */}
          <div
            className="hidden lg:block absolute bottom-[30%] right-2 sm:right-4 md:right-6 lg:right-8 xl:right-12 z-[26] animate-float"
            style={{ animationDelay: '5.5s' }}
          >
            <div className="bg-[#FFEBE6] border-2 border-[#FFB4A0] rounded-full px-4 lg:px-5 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs lg:text-sm xl:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Perfect for<br />any occasion
              </p>
            </div>
          </div>

          {/* Between 4:30 and 6 - Bottom Right */}
          <div
            className="hidden md:block absolute bottom-4 sm:bottom-6 md:bottom-8 right-[30%] z-[25] animate-float"
            style={{ animationDelay: '6s' }}
          >
            <div className="bg-[#FFEBE6] border-2 border-[#FFB4A0] rounded-full px-4 md:px-6 lg:px-7 py-1.5 md:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs md:text-sm lg:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Every lamp tells<br />a unique story
              </p>
            </div>
          </div>

          {/* Between 6 and 7:30 - Bottom Left */}
          <div
            className="hidden md:block absolute bottom-4 sm:bottom-6 md:bottom-8 left-[30%] z-[24] animate-float"
            style={{ animationDelay: '1.2s' }}
          >
            <div className="bg-[#FFF9E6] border-2 border-[#FFD93D] rounded-full px-4 md:px-6 lg:px-7 py-1.5 md:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs md:text-sm lg:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                One-of-a-kind<br />gift
              </p>
            </div>
          </div>

          {/* Between 7:30 and 9 - Lower Left */}
          <div
            className="hidden lg:block absolute bottom-[30%] left-2 sm:left-4 md:left-6 lg:left-8 xl:left-12 z-[23] animate-float"
            style={{ animationDelay: '2.8s' }}
          >
            <div className="bg-[#FFEBE6] border-2 border-[#FFB4A0] rounded-full px-4 lg:px-5 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs lg:text-sm xl:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Add personal touch<br />to any room
              </p>
            </div>
          </div>

          {/* Between 9 and 10:30 - Upper Left */}
          <div
            className="hidden lg:block absolute top-[30%] left-2 sm:left-4 md:left-6 lg:left-8 xl:left-12 z-[22] animate-float"
            style={{ animationDelay: '4.2s' }}
          >
            <div className="bg-[#FFF9E6] border-2 border-[#FFD93D] rounded-full px-4 lg:px-5 xl:px-6 py-1.5 lg:py-2 xl:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs lg:text-sm xl:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                A heartwarming<br />keepsake
              </p>
            </div>
          </div>

          {/* Between 10:30 and 12 - Top Left */}
          <div
            className="hidden md:block absolute top-4 sm:top-6 md:top-8 left-[30%] z-[21] animate-float"
            style={{ animationDelay: '5.8s' }}
          >
            <div className="bg-[#FFEBE6] border-2 border-[#FFB4A0] rounded-full px-4 md:px-6 lg:px-7 py-1.5 md:py-2.5 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs md:text-sm lg:text-base font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Magic of light<br />& shadow
              </p>
            </div>
          </div>

          {/* Additional callouts at edges - Hidden on small and medium screens */}
          <div
            className="hidden xl:block absolute top-1/2 -translate-y-1/2 right-1 sm:right-2 md:right-3 z-[29] animate-float"
            style={{ animationDelay: '6.5s' }}
          >
            <div className="bg-[#FFF9E6] border-2 border-[#FFD93D] rounded-full px-4 xl:px-5 py-1.5 xl:py-2 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs xl:text-sm font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Sophisticated<br />decor piece
              </p>
            </div>
          </div>

          <div
            className="hidden xl:block absolute top-1/2 -translate-y-1/2 left-1 sm:left-2 md:left-3 z-[29] animate-float"
            style={{ animationDelay: '3.2s' }}
          >
            <div className="bg-[#FFEBE6] border-2 border-[#FFB4A0] rounded-full px-4 xl:px-5 py-1.5 xl:py-2 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <p className="text-xs xl:text-sm font-semibold text-gray-700 text-center leading-snug whitespace-nowrap">
                Relive memories<br />every day
              </p>
            </div>
          </div>

          {/* Animated Stars - Yellow (Large) */}
          <div className="absolute top-10 left-20 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 animate-twinkle" style={{ animationDelay: '0s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-20 right-32 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 animate-twinkle" style={{ animationDelay: '1.5s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute bottom-32 left-16 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 animate-twinkle" style={{ animationDelay: '3s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute bottom-20 right-20 w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 animate-twinkle" style={{ animationDelay: '2s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-1/3 left-12 w-10 h-10 sm:w-12 sm:h-12 animate-twinkle" style={{ animationDelay: '4.5s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-2/3 right-16 w-12 h-12 sm:w-14 sm:h-14 animate-twinkle" style={{ animationDelay: '5.2s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute bottom-1/4 left-24 w-8 h-8 sm:w-10 sm:h-10 animate-twinkle" style={{ animationDelay: '6s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-1/4 right-28 w-10 h-10 sm:w-12 sm:h-12 animate-twinkle" style={{ animationDelay: '3.8s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>

          {/* More Yellow Stars - Medium & Small */}
          <div className="hidden md:block absolute top-40 left-1/4 w-8 h-8 md:w-10 md:h-10 animate-twinkle" style={{ animationDelay: '1.8s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden md:block absolute bottom-48 right-1/4 w-10 h-10 md:w-12 md:h-12 animate-twinkle" style={{ animationDelay: '4.2s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden lg:block absolute top-1/2 left-8 w-8 h-8 animate-twinkle" style={{ animationDelay: '2.2s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden lg:block absolute top-1/2 right-8 w-8 h-8 animate-twinkle" style={{ animationDelay: '5.5s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden xl:block absolute top-16 left-1/3 w-6 h-6 animate-twinkle" style={{ animationDelay: '3.2s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden xl:block absolute bottom-24 right-1/3 w-6 h-6 animate-twinkle" style={{ animationDelay: '4.8s' }}>
            <img src="/images/star-yellow.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>

          {/* Animated Stars - Red/Coral (Small accent stars) */}
          <div className="absolute top-32 right-16 w-6 h-6 sm:w-8 sm:h-8 animate-twinkle" style={{ animationDelay: '1s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-1/2 left-32 w-6 h-6 sm:w-8 sm:h-8 animate-twinkle" style={{ animationDelay: '4s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute bottom-40 right-40 w-6 h-6 sm:w-8 sm:h-8 animate-twinkle" style={{ animationDelay: '2.5s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-16 left-1/2 w-6 h-6 sm:w-8 sm:h-8 animate-twinkle" style={{ animationDelay: '3.5s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-24 left-40 w-5 h-5 sm:w-6 sm:h-6 animate-twinkle" style={{ animationDelay: '2.8s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute bottom-28 left-36 w-5 h-5 sm:w-6 sm:h-6 animate-twinkle" style={{ animationDelay: '5.8s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute top-52 right-24 w-5 h-5 sm:w-6 sm:h-6 animate-twinkle" style={{ animationDelay: '1.8s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="absolute bottom-52 right-12 w-5 h-5 sm:w-6 sm:h-6 animate-twinkle" style={{ animationDelay: '4.8s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>

          {/* More Red Stars - Scattered */}
          <div className="hidden md:block absolute top-1/4 left-20 w-5 h-5 animate-twinkle" style={{ animationDelay: '3.2s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden md:block absolute top-3/4 right-36 w-5 h-5 animate-twinkle" style={{ animationDelay: '6.2s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden lg:block absolute top-36 left-1/3 w-4 h-4 animate-twinkle" style={{ animationDelay: '2.2s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden lg:block absolute bottom-36 right-1/3 w-4 h-4 animate-twinkle" style={{ animationDelay: '5.2s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden lg:block absolute top-1/3 right-12 w-4 h-4 animate-twinkle" style={{ animationDelay: '1.5s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden lg:block absolute bottom-1/3 left-12 w-4 h-4 animate-twinkle" style={{ animationDelay: '4.5s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden xl:block absolute top-44 right-1/4 w-4 h-4 animate-twinkle" style={{ animationDelay: '3.8s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden xl:block absolute bottom-44 left-1/4 w-4 h-4 animate-twinkle" style={{ animationDelay: '6.8s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden xl:block absolute top-60 left-16 w-4 h-4 animate-twinkle" style={{ animationDelay: '2.5s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div className="hidden xl:block absolute bottom-60 right-16 w-4 h-4 animate-twinkle" style={{ animationDelay: '5.5s' }}>
            <img src="/images/star-red.svg" alt="" className="w-full h-full" onError={(e) => e.target.style.display = 'none'} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
